import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Gift } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, FALLBACK_PRODUCTS } from "@/data/content";
import { easeOutExpo } from "@/lib/motion";
import { API, waLink, productWaMsg } from "@/lib/site";

function QuickView({ p, onClose }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [date, setDate] = useState("");

  const msg = productWaMsg(p, { customName: name, qty, date });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="quick-view-drawer"
    >
      <div className="absolute inset-0 bg-noir/70 backdrop-blur-sm" onClick={onClose} data-testid="quick-view-overlay" />
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 60, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.45, ease: easeOutExpo }}
        className="relative bg-ivory w-full sm:max-w-3xl rounded-t-3xl sm:rounded-3xl overflow-hidden border border-gold/30 shadow-2xl max-h-[92vh] overflow-y-auto"
        data-testid="quick-view-panel"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-ivory/90 border border-gold/30 flex items-center justify-center hover:bg-crimson hover:text-ivory transition-colors duration-300"
          aria-label="Close quick view"
          data-testid="quick-view-close-button"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="grid sm:grid-cols-2">
          <div className="relative h-64 sm:h-auto">
            <img src={p.image} alt={p.name} className="img-warm absolute inset-0 w-full h-full object-cover" data-testid="quick-view-image" />
            {p.badge && (
              <span className="absolute top-4 left-4 rounded-full bg-gold text-charcoal font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-1.5" data-testid="quick-view-badge">
                {p.badge}
              </span>
            )}
          </div>
          <div className="p-6 sm:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">{p.category} · {p.sku}</p>
            <h3 className="mt-3 font-display text-3xl font-semibold text-charcoal" data-testid="quick-view-title">{p.name}</h3>
            <p className="mt-3 text-sm leading-relaxed text-clay">{p.description}</p>
            <p className="mt-4 font-mono text-xl text-crimson" data-testid="quick-view-price">₹{p.price.toLocaleString("en-IN")}</p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Personalize (optional)</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name, logo note, message…"
                  className="mt-1.5 w-full rounded-xl border border-gold/40 bg-white px-4 py-3 text-sm outline-none focus:border-crimson transition-colors duration-300"
                  data-testid="quick-view-personalize-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-gold/40 bg-white px-4 py-3 text-sm outline-none focus:border-crimson transition-colors duration-300"
                    data-testid="quick-view-quantity-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Needed by</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-gold/40 bg-white px-4 py-3 text-sm outline-none focus:border-crimson transition-colors duration-300"
                    data-testid="quick-view-date-input"
                  />
                </div>
              </div>
            </div>

            <a
              href={waLink(msg)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => toast.success("Opening WhatsApp with your gift request…")}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-crimson text-ivory px-6 py-4 text-sm font-semibold hover:bg-crimson-dark transition-colors duration-300"
              data-testid="quick-view-whatsapp-button"
            >
              <MessageCircle className="h-4 w-4" />
              Send this on WhatsApp
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ProductCard({ p, i, onQuickView }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.6, delay: (i % 4) * 0.07, ease: easeOutExpo }}
      className="group"
      data-testid={`product-card-${p.sku}`}
    >
      <div className="arch-sm relative overflow-hidden border border-gold/25 bg-sand cursor-pointer" onClick={onQuickView} data-testid={`product-image-${p.sku}`}>
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="img-warm w-full h-72 sm:h-80 object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.07]"
        />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(90%_70%_at_50%_20%,rgba(230,198,101,0.22),transparent_65%)]" />
        {p.badge && (
          <span className="absolute top-4 left-4 rounded-full bg-noir/85 backdrop-blur text-gold-light font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-1.5" data-testid={`product-badge-${p.sku}`}>
            {p.badge}
          </span>
        )}
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-ivory/95 border border-gold/40 text-charcoal text-xs font-semibold px-5 py-2.5 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-400 whitespace-nowrap">
          Quick view
        </span>
      </div>
      <div className="pt-4 px-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terracotta" data-testid={`product-meta-${p.sku}`}>
          {p.category} · {p.sku}
        </p>
        <div className="mt-1.5 flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-semibold text-charcoal leading-snug" data-testid={`product-name-${p.sku}`}>
            {p.name}
          </h3>
          <p className="font-mono text-base text-crimson whitespace-nowrap pt-0.5" data-testid={`product-price-${p.sku}`}>
            ₹{p.price.toLocaleString("en-IN")}
          </p>
        </div>
        <button
          onClick={() => window.open(waLink(productWaMsg(p)), "_blank")}
          className="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-crimson hover:gap-3 transition-[gap,color] duration-300"
          data-testid={`product-inquire-${p.sku}`}
        >
          <MessageCircle className="h-4 w-4" />
          Inquire on WhatsApp
        </button>
      </div>
    </motion.article>
  );
}

export default function Products({ filter, onFilterChange }) {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((data) => {
        if (alive && Array.isArray(data) && data.length) {
          setProducts(data.map((d) => ({ ...d, id: d.id || d.sku })));
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const tabs = ["All", ...CATEGORIES];
  const shown = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="products" className="py-16 md:py-24 lg:py-28 scroll-mt-16 bg-sand/60 border-y border-gold/20" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.75, ease: easeOutExpo }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson" data-testid="products-eyebrow">The Gift Catalog</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-charcoal" data-testid="products-heading">
              Curated with <span className="italic text-terracotta">care</span>
            </h2>
          </motion.div>
          <p className="max-w-sm text-sm leading-relaxed text-clay" data-testid="products-sub">
            Every piece can be personalized — names, logos, messages, colors.
            Tap a gift to customize, then order on WhatsApp.
          </p>
        </div>

        <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="product-filter-tabs">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => onFilterChange(t)}
              className={`shrink-0 rounded-full px-5 py-2.5 text-[13px] font-semibold border transition-[background-color,color,border-color] duration-300 ${
                filter === t
                  ? "bg-charcoal text-ivory border-charcoal"
                  : "bg-white/70 text-charcoal/75 border-gold/30 hover:border-crimson hover:text-crimson"
              }`}
              data-testid={`product-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}-tab`}
            >
              {t}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-7 gap-y-12">
          <AnimatePresence mode="popLayout">
            {shown.map((p, i) => (
              <ProductCard key={p.sku} p={p} i={i} onQuickView={() => setSelected(p)} />
            ))}
          </AnimatePresence>
        </motion.div>

        {shown.length === 0 && (
          <div className="py-20 text-center" data-testid="products-empty-state">
            <Gift className="h-10 w-10 mx-auto text-gold" />
            <p className="mt-4 font-display text-2xl text-charcoal">New collection being gift-wrapped…</p>
            <p className="mt-2 text-sm text-clay">Ask us on WhatsApp — we curate custom hampers daily.</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && <QuickView p={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
